import { enableMapSet } from 'immer'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { createSelectors } from './factories/createSelectors'

enableMapSet()

/**
 * Entity types that can have deferred updates
 */
export type DeferredEntityType =
  | 'task'
  | 'entity'
  | 'project'
  | 'contact'
  | 'deal'
  | 'form'
  | 'document'
  | 'collection'
  | 'note'
  | 'member'
  | 'collectionEntry'
  | 'event'

/**
 * Deferred update for a row that has an open popover
 */
export interface DeferredRowUpdate {
  entityType: DeferredEntityType
  updates: Record<string, unknown>
  timestamp: number
}

/**
 * Global table editing state manager.
 *
 * This store tracks which cells have open popovers across all tables,
 * enabling real-time updates to skip rows with active user interactions.
 *
 * Key features:
 * - Track open popovers by tableId, rowId, and cellId
 * - Provide O(1) lookups for row-level interaction checks
 * - Queue deferred updates for rows with open popovers
 * - Clean up stale deferred updates automatically
 */
interface TableEditingState {
  // Map of tableId → Map of rowId → Set of cellIds with open popovers
  openPopovers: Map<string, Map<string, Set<string>>>

  // Map of rowId → Set of tableIds where this row has open popovers
  // (Allows quick lookup regardless of which table)
  rowsWithOpenPopovers: Map<string, Set<string>>

  // Deferred updates for rows that have open popovers
  // rowId → DeferredRowUpdate
  deferredUpdates: Map<string, DeferredRowUpdate>

  // Actions
  registerPopoverOpen: (tableId: string, rowId: string, cellId: string) => void
  registerPopoverClose: (tableId: string, rowId: string, cellId: string) => void

  // Query methods
  hasOpenPopover: (tableId: string, rowId?: string) => boolean
  hasOpenPopoverForRow: (rowId: string) => boolean
  hasAnyOpenPopover: () => boolean
  getRowsWithOpenPopovers: (tableId: string) => Set<string>
  getCellsWithOpenPopovers: (tableId: string, rowId: string) => Set<string>
  isCellPopoverOpen: (cellId: string) => boolean

  // Deferred update management
  queueDeferredUpdate: (
    entityType: DeferredEntityType,
    rowId: string,
    updates: Record<string, unknown>,
  ) => void
  getDeferredUpdatesByType: (
    entityType: DeferredEntityType,
  ) => Map<string, DeferredRowUpdate>
  getDeferredUpdate: (rowId: string) => DeferredRowUpdate | undefined
  clearDeferredUpdate: (rowId: string) => DeferredRowUpdate | undefined
  hasDeferredUpdate: (rowId: string) => boolean
  cleanupStaleUpdates: (maxAgeMs?: number) => void

  // Utility
  clearAll: () => void
}

// Configuration
const DEFAULT_STALE_UPDATE_AGE_MS = 30000 // 30 seconds

const baseTableEditingStore = create<TableEditingState>()(
  immer((set, get) => ({
    openPopovers: new Map(),
    rowsWithOpenPopovers: new Map(),
    deferredUpdates: new Map(),

    registerPopoverOpen: (tableId: string, rowId: string, cellId: string) => {
      set(state => {
        // Initialize table map if needed
        if (!state.openPopovers.has(tableId)) {
          state.openPopovers.set(tableId, new Map())
        }
        const tableMap = state.openPopovers.get(tableId)!

        // Initialize row set if needed
        if (!tableMap.has(rowId)) {
          tableMap.set(rowId, new Set())
        }
        tableMap.get(rowId)!.add(cellId)

        // Update row-level index
        if (!state.rowsWithOpenPopovers.has(rowId)) {
          state.rowsWithOpenPopovers.set(rowId, new Set())
        }
        state.rowsWithOpenPopovers.get(rowId)!.add(tableId)
      })
    },

    registerPopoverClose: (tableId: string, rowId: string, cellId: string) => {
      set(state => {
        const tableMap = state.openPopovers.get(tableId)
        if (!tableMap) return

        const rowSet = tableMap.get(rowId)
        if (!rowSet) return

        rowSet.delete(cellId)

        // Clean up empty sets
        if (rowSet.size === 0) {
          tableMap.delete(rowId)

          // Update row-level index
          const rowTables = state.rowsWithOpenPopovers.get(rowId)
          if (rowTables) {
            rowTables.delete(tableId)
            if (rowTables.size === 0) {
              state.rowsWithOpenPopovers.delete(rowId)
            }
          }
        }

        // Clean up empty table maps
        if (tableMap.size === 0) {
          state.openPopovers.delete(tableId)
        }
      })
    },

    hasOpenPopover: (tableId: string, rowId?: string): boolean => {
      const state = get()
      const tableMap = state.openPopovers.get(tableId)
      if (!tableMap) return false

      if (rowId) {
        const rowSet = tableMap.get(rowId)
        return rowSet ? rowSet.size > 0 : false
      }

      // Check if any row in this table has open popovers
      for (const rowSet of tableMap.values()) {
        if (rowSet.size > 0) return true
      }
      return false
    },

    hasOpenPopoverForRow: (rowId: string): boolean => {
      const state = get()
      const rowTables = state.rowsWithOpenPopovers.get(rowId)
      return rowTables ? rowTables.size > 0 : false
    },

    /**
     * Check if ANY popover is open across ALL tables.
     * This is used to defer real-time updates when any editing is in progress.
     */
    hasAnyOpenPopover: (): boolean => {
      const state = get()
      return state.rowsWithOpenPopovers.size > 0
    },

    getRowsWithOpenPopovers: (tableId: string): Set<string> => {
      const state = get()
      const tableMap = state.openPopovers.get(tableId)
      if (!tableMap) return new Set()
      return new Set(tableMap.keys())
    },

    getCellsWithOpenPopovers: (tableId: string, rowId: string): Set<string> => {
      const state = get()
      const tableMap = state.openPopovers.get(tableId)
      if (!tableMap) return new Set()
      return tableMap.get(rowId) || new Set()
    },

    /**
     * Check if a specific cell's popover is registered as open.
     * This is used by components to restore their open state after remount.
     */
    isCellPopoverOpen: (cellId: string): boolean => {
      const state = get()
      // Search through all tables and rows for this cellId
      for (const tableMap of state.openPopovers.values()) {
        for (const rowSet of tableMap.values()) {
          if (rowSet.has(cellId)) {
            return true
          }
        }
      }
      return false
    },

    queueDeferredUpdate: (
      entityType: DeferredEntityType,
      rowId: string,
      updates: Record<string, unknown>,
    ) => {
      set(state => {
        const existing = state.deferredUpdates.get(rowId)
        if (existing && existing.entityType === entityType) {
          // Merge updates for same entity type, newer values overwrite older
          state.deferredUpdates.set(rowId, {
            entityType,
            updates: { ...existing.updates, ...updates },
            timestamp: Date.now(),
          })
        } else {
          state.deferredUpdates.set(rowId, {
            entityType,
            updates,
            timestamp: Date.now(),
          })
        }
      })
    },

    getDeferredUpdatesByType: (
      entityType: DeferredEntityType,
    ): Map<string, DeferredRowUpdate> => {
      const state = get()
      const result = new Map<string, DeferredRowUpdate>()
      state.deferredUpdates.forEach((update, rowId) => {
        if (update.entityType === entityType) {
          result.set(rowId, update)
        }
      })
      return result
    },

    getDeferredUpdate: (rowId: string): DeferredRowUpdate | undefined => {
      return get().deferredUpdates.get(rowId)
    },

    clearDeferredUpdate: (rowId: string): DeferredRowUpdate | undefined => {
      const state = get()
      const update = state.deferredUpdates.get(rowId)
      if (update) {
        set(draft => {
          draft.deferredUpdates.delete(rowId)
        })
      }
      return update
    },

    hasDeferredUpdate: (rowId: string): boolean => {
      return get().deferredUpdates.has(rowId)
    },

    cleanupStaleUpdates: (maxAgeMs = DEFAULT_STALE_UPDATE_AGE_MS) => {
      const now = Date.now()
      const currentState = get()
      set(state => {
        const toDelete: string[] = []
        state.deferredUpdates.forEach((update, rowId) => {
          // An editor held open longer than maxAge is NOT stale — the update
          // is still legitimately deferred and will flush when the editor
          // closes. Deleting it here would silently drop the remote change.
          if (currentState.hasOpenPopoverForRow(rowId)) {
            return
          }
          if (now - update.timestamp > maxAgeMs) {
            toDelete.push(rowId)
          }
        })
        toDelete.forEach(rowId => {
          state.deferredUpdates.delete(rowId)
        })
      })
    },

    clearAll: () => {
      set(state => {
        state.openPopovers.clear()
        state.rowsWithOpenPopovers.clear()
        state.deferredUpdates.clear()
      })
    },
  })),
)

export const useTableEditingStore = createSelectors(baseTableEditingStore)

// Export direct access to store for non-React contexts (e.g. realtime handlers)
export const tableEditingStore = baseTableEditingStore
